/**
 * DetalleBonificacionPage — detalle completo de un reembolso, datos reales.
 *
 * Conectado al backend (`useClaim`). Compone:
 *  - PageHeader con back, número de solicitud y status badge.
 *  - Stats: Solicitado / Reembolso estimado / Aprobado / Pagado.
 *  - Grid: Mascota, Veterinario, Diagnóstico+tratamiento, Comentario.
 *  - Lista de Prestaciones con cálculo de reembolso esperado por item
 *    (porcentaje del plan + cap por evento en UF → CLP).
 *  - Sección de Documentos: formulario PDF + boletas (PDF/imagen) con preview.
 *  - Notas del equipo / motivo de rechazo si aplica.
 */

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { claimsApi } from '@modules/claims/infrastructure/repositories/http/ClaimsHttpRepository';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@app/components/common/PageHeader';
import { EmptyState } from '@app/components/common/EmptyState';
import {
  StatusBadge,
  variantForStatus,
  labelForStatus,
} from '@app/components/common/StatusBadge';
import { DocumentPreview } from '@app/components/reimbursements/DocumentPreview';
import {
  calculateReimbursement,
  coverageNameFromPlan,
} from '@app/components/reimbursements/reimbursement-calc';
import { useClaim } from '@modules/claims/presentation/hooks/useClaims';
import { usePet } from '@modules/pets/presentation/hooks/usePets';
import { useSubscription } from '@modules/billing/presentation/hooks/useBilling';
import { usePlan, usePlanDetail } from '@modules/plans/presentation/hooks/usePlans';
import { formatCLP, formatDate, formatUF } from '@shared/utils/formatters';
import {
  ArrowLeft,
  PawPrint,
  Stethoscope,
  FileText,
  Receipt,
  AlertCircle,
  MessageSquare,
  Calculator,
  Sparkles,
} from 'lucide-react';

export const DetalleBonificacionPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const { data: claim, isLoading, error } = useClaim(id);
  const { data: petDetail } = usePet(claim?.petId);
  const pet = petDetail?.pet;
  const { data: subscription } = useSubscription(claim?.subscriptionId);
  const { data: planDetail } = usePlanDetail(subscription?.planId);
  const { data: planWithCoverages } = usePlan(subscription?.planId);

  const uploadMissing = async (kind: 'form' | 'invoice', file: File | undefined) => {
    if (!file || !id) return;
    if (kind === 'form' && (!/\.pdf$/i.test(file.name) || file.size > 20 * 1024 * 1024)) {
      toast.error('El formulario debe ser un PDF de hasta 20 MB.');
      return;
    }
    if (kind === 'invoice' && (!/\.(pdf|jpe?g|png)$/i.test(file.name) || file.size > 5 * 1024 * 1024)) {
      toast.error('La boleta debe ser PDF, JPG o PNG de hasta 5 MB.');
      return;
    }
    setUploading(true);
    try {
      if (kind === 'form') await claimsApi.uploadRequestForm(id, file);
      else await claimsApi.uploadInvoice(id, file);
      await queryClient.invalidateQueries({ queryKey: ['claims', id] });
      toast.success('Documento subido correctamente');
    } catch { toast.error('No se pudo subir el documento. Verifica el formato y tamaño e inténtalo nuevamente.'); }
    finally { setUploading(false); }
  };

  /* ─── Cálculo de reembolso esperado por item y total ─── */
  const breakdown = useMemo(() => {
    if (!claim?.items) return [];
    return claim.items.map((item) => {
      const coverageId = planWithCoverages?.coverages?.find(
        (coverage) => coverage.coverageTypeId === item.coverageTypeId,
      )?.id ?? item.coverageTypeId;
      const calc = calculateReimbursement({
        amountClp: item.amountClp,
        coverageTypeId: coverageId,
        plan: planDetail,
      });
      return {
        item,
        calc,
        coverageId,
        coverageName: coverageNameFromPlan(coverageId, planDetail) ?? 'Cobertura',
      };
    });
  }, [claim?.items, planDetail, planWithCoverages]);

  const totalEstimated = useMemo(
    () => breakdown.reduce((sum, b) => sum + b.calc.estimatedReimbursableClp, 0),
    [breakdown],
  );

  /* ─── Loading / error ─── */
  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-24 rounded-full" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Skeleton className="h-48 w-full rounded-2xl" />
          <Skeleton className="h-48 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error || !claim) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 rounded-full text-muted-foreground hover:text-foreground"
          onClick={() => navigate('/bonificaciones')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
          Volver
        </Button>
        <EmptyState
          icon={AlertCircle}
          title="No pudimos cargar el reembolso"
          description="Puede que el enlace esté incorrecto o que la solicitud haya sido eliminada."
          action={
            <Button asChild variant="outline" className="rounded-full">
              <a href="/bonificaciones">Ver mis reembolsos</a>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 rounded-full text-muted-foreground hover:text-foreground"
        onClick={() => navigate('/bonificaciones')}
      >
        <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
        Volver a Reembolsos
      </Button>

      <PageHeader
        title={`Solicitud N° ${claim.claimNumber}`}
        subtitle={`Enviada el ${formatDate(claim.submittedAt)}`}
        action={
          <StatusBadge variant={variantForStatus(claim.status)}>
            {labelForStatus(claim.status)}
          </StatusBadge>
        }
      />

      {/* ─── Stats / Montos ─── */}
      <section className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <SummaryStat
          label="Solicitado"
          value={formatCLP(claim.totalAmountClp)}
          tone="default"
        />
        <SummaryStat
          label="Reembolso estimado"
          value={
            planDetail
              ? formatCLP(totalEstimated)
              : <span className="text-xs text-muted-foreground">Cargando plan…</span>
          }
          tone="sidebar"
          hint={planDetail ? 'Según porcentajes del plan' : undefined}
        />
        <SummaryStat
          label="Aprobado"
          value={claim.approvedAmountClp != null ? formatCLP(claim.approvedAmountClp) : '—'}
          tone={claim.approvedAmountClp != null ? 'success' : 'muted'}
        />
        <SummaryStat
          label="Reembolsado"
          value={claim.reimbursedAmountClp != null ? formatCLP(claim.reimbursedAmountClp) : '—'}
          tone={claim.reimbursedAmountClp != null ? 'success' : 'muted'}
        />
      </section>

      {/* ─── Info principal en grid 2 cols ─── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Mascota */}
        <Section icon={PawPrint} title="Mascota">
          {pet ? (
            <dl className="space-y-2">
              <Row label="Nombre" value={pet.name} />
              <Row label="Especie" value={pet.species} />
              {pet.breed && <Row label="Raza" value={pet.breed} />}
              {pet.birthDate && <Row label="Nacimiento" value={formatDate(pet.birthDate)} />}
              {subscription?.planName && (
                <Row label="Plan" value={subscription.planName} />
              )}
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">Cargando datos de la mascota…</p>
          )}
        </Section>

        {/* Veterinario */}
        <Section icon={Stethoscope} title="Atención veterinaria">
          <dl className="space-y-2">
            <Row label="Veterinario" value={claim.vetName} />
            {claim.vetClinic && <Row label="Clínica" value={claim.vetClinic} />}
            <Row label="Fecha atención" value={formatDate(claim.attentionDate.slice(0, 10))} />
          </dl>
        </Section>

        {/* Diagnóstico (span 2 cols) */}
        <Section icon={FileText} title="Diagnóstico" className="lg:col-span-2">
          <p className="whitespace-pre-line break-words [overflow-wrap:anywhere] text-sm leading-relaxed text-foreground">
            {claim.diagnosis}
          </p>
          {claim.treatmentDescription && (
            <>
              <p className="mt-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Tratamiento
              </p>
              <p className="mt-1 whitespace-pre-line break-words [overflow-wrap:anywhere] text-sm leading-relaxed text-foreground">
                {claim.treatmentDescription}
              </p>
            </>
          )}
        </Section>

        {/* Prestaciones con cálculo de reembolso */}
        <Section icon={Receipt} title="Prestaciones y reembolso" className="lg:col-span-2">
          {!claim.items || claim.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay prestaciones registradas.</p>
          ) : !planDetail ? (
            <div className="space-y-2">
              <Skeleton className="h-20 w-full rounded-xl" />
              <p className="text-xs text-muted-foreground">
                Cargando información del plan para calcular el reembolso esperado…
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {breakdown.map(({ item, calc, coverageName, coverageId }, idx) => (
                  <PrestationCard
                    key={item.id ?? idx}
                    coverageName={coverageName}
                    description={item.description}
                    amountClp={item.amountClp}
                    approvedAmountClp={item.approvedAmountClp ?? null}
                    coveragePercentage={calc.coveragePercentage}
                    capPerEventClp={calc.capPerEventClp}
                    capUfPerEvent={
                      planDetail.coverages.find((c) => c.id === coverageId)
                        ?.cap_uf_per_event ?? null
                    }
                    beforeCapClp={calc.beforeCapClp}
                    estimatedReimbursableClp={calc.estimatedReimbursableClp}
                    cappedByEvent={calc.cappedByEvent}
                    noCoverage={calc.noCoverage}
                  />
                ))}
              </div>

              {/* Totales */}
              <div className="mt-5 grid grid-cols-2 gap-3 rounded-xl border border-border bg-muted/30 p-4 sm:grid-cols-3">
                <TotalRow label="Total solicitado" value={formatCLP(claim.totalAmountClp)} />
                <TotalRow
                  label="Reembolso estimado"
                  value={formatCLP(totalEstimated)}
                  highlight
                />
                {planDetail.uf_value_today > 0 && (
                  <TotalRow
                    label="UF de hoy"
                    value={formatCLP(planDetail.uf_value_today)}
                    muted
                  />
                )}
              </div>

              <div className="mt-3 flex items-start gap-2 rounded-xl border border-sidebar-primary/20 bg-sidebar-accent/40 p-3">
                <Calculator
                  className="mt-0.5 h-4 w-4 shrink-0 text-sidebar-primary"
                  aria-hidden="true"
                />
                <div className="space-y-0.5 text-xs">
                  <p className="font-semibold text-foreground">
                    ¿Cómo calculamos el reembolso?
                  </p>
                  <p className="text-muted-foreground">
                    Para cada prestación aplicamos el porcentaje de tu plan al monto solicitado y,
                    si tu plan tiene un tope por evento, recortamos el resultado a ese tope
                    (convertido de UF a CLP con el valor de hoy). Es una estimación: el monto
                    final lo define el equipo al revisar tus documentos.
                  </p>
                </div>
              </div>
            </>
          )}
        </Section>

        {/* Documentos */}
        <Section icon={FileText} title="Documentos" className="lg:col-span-2">
            <div className="space-y-4">
              {claim.requestFile && (
                <DocumentPreview
                  url={claim.requestFile}
                  label="Formulario de solicitud"
                  fullWidth
                />
              )}
              {!claim.requestFile && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-semibold">Falta el formulario de solicitud</p>
                  <p>El formulario aún no está adjunto a esta bonificación.</p>
                  {['PENDING', 'UNDER_REVIEW'].includes(claim.status) && (
                    <label className="mt-3 block font-medium">Subir formulario PDF (máximo 20 MB)
                      <input className="mt-2 block w-full" type="file" accept=".pdf" disabled={uploading} onChange={e => { void uploadMissing('form', e.target.files?.[0]); e.target.value = ''; }} />
                    </label>
                  )}
                </div>
              )}
              {claim.invoices?.map((inv, i) => (
                <DocumentPreview
                  key={inv.id}
                  url={inv.fileUrl}
                  label={`Boleta / Factura ${i + 1}`}
                  fullWidth
                />
              ))}
              {['PENDING', 'UNDER_REVIEW'].includes(claim.status) && (
                <label className="block text-sm">Agregar boleta PDF, JPG o PNG (máximo 5 MB)
                  <input className="mt-2 block w-full" type="file" accept=".pdf,.jpg,.jpeg,.png" disabled={uploading} onChange={e => { void uploadMissing('invoice', e.target.files?.[0]); e.target.value = ''; }} />
                </label>
              )}
              {uploading && <p className="text-sm">Subiendo documento…</p>}
            </div>
        </Section>

        {/* Notas del equipo / rechazo */}
        {(claim.adminNotes || claim.rejectionReason) && (
          <Section icon={Sparkles} title="Notas del equipo" className="lg:col-span-2">
            {claim.rejectionReason && (
              <div className="mb-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm">
                <p className="font-medium text-destructive">Motivo del rechazo</p>
                <p className="mt-1 text-foreground/80">{claim.rejectionReason}</p>
              </div>
            )}
            {claim.adminNotes && (
              <p className="whitespace-pre-line [overflow-wrap:anywhere] text-sm text-foreground">{claim.adminNotes}</p>
            )}
          </Section>
        )}

        {/* Tu comentario */}
        {claim.comment && (
          <Section icon={MessageSquare} title="Tu comentario" className="lg:col-span-2">
            <p className="whitespace-pre-line [overflow-wrap:anywhere] text-sm text-foreground">{claim.comment}</p>
          </Section>
        )}
      </div>
    </div>
  );
};

/* ─── helpers ─── */

interface SectionProps {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number; 'aria-hidden'?: boolean }>;
  title: string;
  className?: string;
  children: React.ReactNode;
}

function Section({ icon: Icon, title, className, children }: SectionProps) {
  return (
    <section
      className={`rounded-2xl border border-border bg-card p-5 shadow-xs sm:p-6 ${className ?? ''}`}
    >
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent text-accent-foreground">
          <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden={true} />
        </span>
        <h2 className="text-base font-bold tracking-tight">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap justify-between gap-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{value}</dd>
    </div>
  );
}

interface SummaryStatProps {
  label: string;
  value: React.ReactNode;
  tone: 'default' | 'success' | 'muted' | 'sidebar';
  hint?: string;
}

function SummaryStat({ label, value, tone, hint }: SummaryStatProps) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-xs sm:p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={`mt-1 text-xl font-bold tabular-nums sm:text-2xl ${
          tone === 'success'
            ? 'text-emerald-600'
            : tone === 'muted'
            ? 'text-muted-foreground'
            : tone === 'sidebar'
            ? 'text-sidebar-primary'
            : 'text-foreground'
        }`}
      >
        {value}
      </p>
      {hint && <p className="mt-0.5 text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

interface PrestationCardProps {
  coverageName: string;
  description: string;
  amountClp: number;
  approvedAmountClp: number | null;
  coveragePercentage: number;
  capPerEventClp: number | null;
  capUfPerEvent: number | null;
  beforeCapClp: number;
  estimatedReimbursableClp: number;
  cappedByEvent: boolean;
  noCoverage: boolean;
}

function PrestationCard(props: PrestationCardProps) {
  const {
    coverageName,
    description,
    amountClp,
    approvedAmountClp,
    coveragePercentage,
    capPerEventClp,
    capUfPerEvent,
    beforeCapClp,
    estimatedReimbursableClp,
    cappedByEvent,
    noCoverage,
  } = props;

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">{coverageName}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Solicitado</p>
          <p className="text-sm font-semibold tabular-nums text-foreground">
            {formatCLP(amountClp)}
          </p>
        </div>
      </div>

      {noCoverage ? (
        <div className="flex items-start gap-2 rounded-lg border border-chart-4/40 bg-chart-4/15 p-2.5 text-xs">
          <AlertCircle
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-chart-4"
            aria-hidden="true"
          />
          <p className="text-foreground/80">
            Esta prestación no está cubierta por tu plan. Reembolso esperado:{' '}
            <strong>{formatCLP(0)}</strong>.
          </p>
        </div>
      ) : (
        <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
          {/* Pasos de cálculo */}
          <CalcStep
            label={`Cobertura del plan (${Math.round(coveragePercentage * 100)}%)`}
            value={formatCLP(beforeCapClp)}
          />
          {capPerEventClp != null && (
            <CalcStep
              label={`Tope por evento (${capUfPerEvent != null ? formatUF(capUfPerEvent) : '—'})`}
              value={formatCLP(capPerEventClp)}
              dim={!cappedByEvent}
              capped={cappedByEvent}
            />
          )}
          <div className="mt-2 flex items-baseline justify-between border-t border-border pt-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Reembolso esperado
            </p>
            <p className="text-base font-bold tabular-nums text-sidebar-primary">
              {formatCLP(estimatedReimbursableClp)}
            </p>
          </div>

          {approvedAmountClp != null && (
            <div className="flex items-baseline justify-between border-t border-emerald-200 pt-2">
              <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">
                Aprobado por el equipo
              </p>
              <p className="text-base font-bold tabular-nums text-emerald-600">
                {formatCLP(approvedAmountClp)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CalcStep({
  label,
  value,
  dim,
  capped,
}: {
  label: string;
  value: string;
  dim?: boolean;
  capped?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between text-xs">
      <span className={dim ? 'text-muted-foreground' : 'text-foreground'}>
        {label}
        {capped && (
          <span className="ml-1.5 rounded-full bg-chart-4/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-chart-4">
            aplicado
          </span>
        )}
      </span>
      <span
        className={`tabular-nums ${
          dim ? 'text-muted-foreground' : 'font-medium text-foreground'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function TotalRow({
  label,
  value,
  highlight,
  muted,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  muted?: boolean;
}) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={`mt-0.5 text-base font-bold tabular-nums ${
          highlight ? 'text-sidebar-primary' : muted ? 'text-muted-foreground' : 'text-foreground'
        }`}
      >
        {value}
      </p>
    </div>
  );
}
